import React, { useEffect, useState, useRef, useCallback, useMemo } from "react";
import { DndContext, closestCenter, KeyboardSensor, PointerSensor, useSensor, useSensors } from "@dnd-kit/core";
import { SortableContext, sortableKeyboardCoordinates, verticalListSortingStrategy } from "@dnd-kit/sortable";
import { TableContainer, Typography, Box, ThemeProvider, Button, TextField, Tooltip, IconButton } from "@mui/material";
import { VariableSizeList } from "react-window";
import { IniInfo, InvalidRule, PackageConfig, PackageConfigSection, SelectedPackage, UnconfiguredRules } from "system/types/interfaces";
import { useApi, useConfiguration, usePolicyEditorPermissions } from "system/hooks";
import { SortableRow } from "components/modules/policy-editor/components/mainPage/applicabilityRulesTab/SortableRow";
import { muiTheme } from "system/styles/muiTheme";
import { Icon } from "@fluentui/react/lib/Icon";
import { AddPackageModal } from "components/modules/policy-editor";
import { useDispatch, useSelector } from "react-redux";
import { policyEditorActions } from "system/store/slices/policy-editor-slice";
import { RootState } from "system/store";
import { processRules, splitConfigurations } from "system/utils";
import { validateRule } from "components/modules/policy-editor/utils/ruleValidator";
import useAppNavigation from "system/hooks/use-app-navigation";
import * as parseRules from "../../../utils/parseRules";
import { RuleRow } from "../../../utils/parseRules";
import { isRowDirty, restoreOriginalCommentFields, restoreOriginalUncommentFields, tagOriginal } from "../../../utils/ruleSignature";
import InvalidRulesModal from "components/modules/policy-editor/components/mainPage/applicabilityRulesTab/InvalidRulesModal";
import PendingChangesModal from "components/modules/policy-editor/components/mainPage/applicabilityRulesTab/PendingChangesModal";
import PackagesNotInReplicateModal, {
  MissingPackageRule,
} from "components/modules/policy-editor/components/mainPage/applicabilityRulesTab/PackagesNotInReplicateModal";
import { isFullPatternMatch, isPartialPatternMatch } from "components/modules/policy-editor/utils/stringExtensions";
import Loading from "components/ui/Loading";
import { defaultNotConfiguredRules } from "components/modules/policy-editor/components/mainPage/clientConfig/defaultNotConfiguredRules";
import { useDebouncedCallback } from "use-debounce";

interface MainApplicabilityRulesTabProps {
  modifiedRows: string[];
  rules: IniInfo[];
  invalidRules: InvalidRule[];
  versionId?: string;
}

interface SearchBarProps {
  searchResultsCount: number;
  currentResultIndex: number;
  onSearch: (term: string) => void;
  onPrev: () => void;
  onNext: () => void;
  onKeyDown: (e: React.KeyboardEvent) => void;
}

const SearchBar = React.memo(
  React.forwardRef<HTMLDivElement, SearchBarProps>(
    ({ searchResultsCount, currentResultIndex, onSearch, onPrev, onNext, onKeyDown }, ref) => {
      const [localTerm, setLocalTerm] = useState("");
      const debouncedSearch = useDebouncedCallback(onSearch, 250);

      const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        const v = e.target.value;
        setLocalTerm(v);
        debouncedSearch(v);
      };

      const handleClear = () => {
        setLocalTerm("");
        debouncedSearch.cancel();
        onSearch("");
      };

      return (
        <Tooltip title="Press Enter to navigate through the results" arrow>
          <TextField
            ref={ref}
            autoFocus
            placeholder="Search..."
            variant="outlined"
            value={localTerm}
            onChange={handleChange}
            onKeyDown={onKeyDown}
            sx={{ marginRight: 2, width: "350px" }}
            InputProps={{
              endAdornment: searchResultsCount > 0 && (
                <Box sx={{ display: "flex", alignItems: "center", gap: 0 }}>
                  <Typography
                    variant="subtitle2"
                    sx={{ padding: "0 8px", color: "#fff", opacity: 0.7, borderRight: "1px solid #cccccc" }}
                  >
                    {currentResultIndex + 1}/{searchResultsCount}
                  </Typography>
                  <IconButton
                    size="small"
                    onClick={onPrev}
                    sx={{ color: "#fff", opacity: 0.7, "&:hover": { color: "#4caf50", opacity: 1 } }}
                  >
                    <Icon iconName="ChevronUp" />
                  </IconButton>
                  <IconButton
                    size="small"
                    onClick={onNext}
                    sx={{ color: "#fff", opacity: 0.7, "&:hover": { color: "#4caf50", opacity: 1 } }}
                  >
                    <Icon iconName="ChevronDown" />
                  </IconButton>
                  <IconButton
                    size="small"
                    onClick={handleClear}
                    sx={{ fontSize: "18px", color: "#fff", opacity: 0.7, "&:hover": { color: "#4caf50", opacity: 1 } }}
                  >
                    <Icon iconName="Cancel" style={{ fontSize: "inherit" }} />
                  </IconButton>
                </Box>
              ),
            }}
          />
        </Tooltip>
      );
    }
  )
);
SearchBar.displayName = "SearchBar";

const MainApplicabilityRulesTab = ({ modifiedRows, rules, invalidRules, versionId }: MainApplicabilityRulesTabProps) => {
  const packages = useSelector((state: RootState) => state.policyEditor.packages);
  const globalCatalog = useSelector((state: RootState) => state.policyEditor.globalCatalog);
  const pendingReplicateChanges = useSelector((state: RootState) => state.policyEditor.pendingReplicateChanges);
  const lastEditedIndex = useSelector((state: RootState) => state.policyEditor.lastEditedIndex);
  const loadedVersionId = useSelector((state: RootState) => state.policyEditor.currentVersionId);

  const [loading, setLoading] = useState<boolean>(false);
  const [loadingMessage, setLoadingMessage] = useState<string>("Retrieving Applicability Rules...");
  const [error, setError] = useState<string | null>(null);
  const { currentTenant } = useConfiguration();
  const { isReadOnly: readOnlyViewer } = usePolicyEditorPermissions();
  const [isModalOpen, setModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState<"add" | "edit">("add");

  const [insertIndex, setInsertIndex] = useState<number | null>(null);

  const [isErrorModalOpen, setErrorModalOpen] = useState(false);
  const [isPackagesMissingModalOpen, setPackagesMissingModalOpen] = useState(false);
  const [isPendingChangesModalOpen, setPendingChangesModalOpen] = useState(false);

  const [searchTerm, setSearchTerm] = useState("");
  const [searchResults, setSearchResults] = useState<number[]>([]);
  const [currentResultIndex, setCurrentResultIndex] = useState(-1);

  const searchInputRef = useRef<HTMLInputElement>(null);
  const [userClosedPackagesMissing, setUserClosedPackagesMissing] = useState(false);

  const dispatch = useDispatch();
  const navigate = useAppNavigation();

  const api = useApi();

  const listRef = useRef<VariableSizeList>(null);

  const packageMap = useMemo(() => {
    const map: { [key: string]: IniInfo } = {};
    // Replicate.ini wins (its packages have customer-specific icons)
    globalCatalog.forEach((pkg) => {
      if (pkg.value) map[pkg.value] = pkg;
    });
    packages.forEach((pkg) => {
      if (pkg.value) map[pkg.value] = pkg;
    });
    return map;
  }, [packages, globalCatalog]);

  const packagesMissingRules = useMemo<MissingPackageRule[]>(() => {
    if (packages.length === 0) return [];
    return rules
      .map((rule, index) => {
        if (rule.isDeleted || rule.isComment || rule.name.includes("-comment-") || !rule.id || !rule.name) {
          return null;
        }
        const ruleName = rule.name.trim();
        if (!ruleName) return null;
        const inReplicate = packages.some((p) => p.value && isPartialPatternMatch(p.value, ruleName));
        const inPending = pendingReplicateChanges.additions.some((a) => isPartialPatternMatch(a, ruleName));
        const willBeRemoved = pendingReplicateChanges.removals.some((r) => isPartialPatternMatch(r, ruleName));
        if ((inReplicate || inPending) && !willBeRemoved) return null;
        if (willBeRemoved && inPending) return null;
        const hasSyntaxError = invalidRules.some((ir) => ir.rule.id === rule.id);
        if (hasSyntaxError) return null;
        return {
          index: index + 1,
          ruleId: rule.id!,
          ruleText: `${rule.name} ${rule.delimiter ?? ""} ${rule.value ?? ""}`.trim(),
          packageName: ruleName,
        } as MissingPackageRule;
      })
      .filter((r): r is MissingPackageRule => r !== null);
  }, [rules, packages, pendingReplicateChanges, invalidRules]);

  useEffect(() => {
    if (readOnlyViewer) return;
    if (userClosedPackagesMissing) return;
    if (loading || packages.length === 0) return;
    if (invalidRules.length > 0 || isErrorModalOpen) return;
    if (packagesMissingRules.length > 0 && !isPackagesMissingModalOpen) {
      setPackagesMissingModalOpen(true);
    }
  }, [readOnlyViewer, loading, packages.length, packagesMissingRules.length, invalidRules.length, isErrorModalOpen, isPackagesMissingModalOpen, userClosedPackagesMissing]);

  const rulesRef = useRef(rules);
  rulesRef.current = rules;

  const handleSearch = useCallback((term: string) => {
    setSearchTerm(term);
    if (!term) {
      setSearchResults([]);
      setCurrentResultIndex(-1);
      return;
    }

    const lowerTerm = term.toLowerCase();
    const results = rulesRef.current
      .map((rule, index) => {
        const isComment = !rule.delimiter && !rule.inlineComment;
        const searchInName = rule.name.toLowerCase().includes(lowerTerm);
        const searchInComment = isComment && rule.value && rule.value.toLowerCase().includes(lowerTerm);
        const searchInDelimiterValue =
          !isComment &&
          `${rule.delimiter} ${rule.value}${rule.inlineComment ? `;${rule.inlineComment}` : ""}`
            .toLowerCase()
            .includes(lowerTerm);
        return searchInName || searchInComment || searchInDelimiterValue ? index : -1;
      })
      .filter((index) => index !== -1);
    setSearchResults(results);
    setCurrentResultIndex(results.length > 0 ? 0 : -1);
    if (results.length > 0) {
      listRef.current?.scrollToItem(results[0], "start");
    }
  }, []);


  const handlePrevResult = useCallback(() => {
    if (searchResults.length === 0) return;
    const prevIndex = (currentResultIndex - 1 + searchResults.length) % searchResults.length;
    setCurrentResultIndex(prevIndex);
    listRef.current?.scrollToItem(searchResults[prevIndex], "start");
  }, [currentResultIndex, searchResults]);

  const handleNextResult = useCallback(() => {
    if (searchResults.length === 0) return;
    const nextIndex = (currentResultIndex + 1) % searchResults.length;
    setCurrentResultIndex(nextIndex);
    listRef.current?.scrollToItem(searchResults[nextIndex], "start");
  }, [currentResultIndex, searchResults]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (searchResults.length === 0) return;

      switch (e.key) {
        case "Enter":
          e.preventDefault();
          const nextIndex = (currentResultIndex + 1) % searchResults.length;
          setCurrentResultIndex(nextIndex);
          listRef.current?.scrollToItem(searchResults[nextIndex], "start");
          break;
        case "ArrowUp":
          e.preventDefault();
          const prevIndex = (currentResultIndex - 1 + searchResults.length) % searchResults.length;
          setCurrentResultIndex(prevIndex);
          listRef.current?.scrollToItem(searchResults[prevIndex], "start");
          break;
        case "ArrowDown":
          e.preventDefault();
          const nextIndexDown = (currentResultIndex + 1) % searchResults.length;
          setCurrentResultIndex(nextIndexDown);
          listRef.current?.scrollToItem(searchResults[nextIndexDown], "start");
          break;
      }
    },
    [currentResultIndex, searchResults]
  );
  const getPackages = useCallback(async () => {
    try {
      const customer = currentTenant?.shortName ?? "";
      const [replicateResponse, catalogResponse] = await Promise.all([
        api.policyEditor.getReplicateIniFile(customer),
        api.packages.getCatalog(customer),
      ]);

      const replicatePackages: PackageConfig[] = replicateResponse?.isSuccess && replicateResponse.value?.packages
        ? replicateResponse.value.packages.map((pkg) => ({
            name: pkg.name,
            value: pkg.value || pkg.name,
            description: pkg.description ?? "",
            iconUrl: pkg.iconUrl,
          }))
        : [];

      const catalogPackages: PackageConfig[] = catalogResponse?.isSuccess && catalogResponse.items
        ? catalogResponse.items.map((pkg) => ({
            name: pkg.name,
            value: pkg.name,
            description: pkg.description ?? "",
            iconUrl: pkg.iconUrl,
          }))
        : [];

      dispatch(policyEditorActions.setPackages(replicatePackages));
      dispatch(policyEditorActions.setGlobalCatalog(catalogPackages));
      return replicatePackages;
    } catch (error) {
      setError("Failed to load packages. Please try again.");
    }
  }, [api.policyEditor, api.packages, currentTenant?.shortName, dispatch]);

  useEffect(() => {
    const handleKeyPress = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key === "/") {
        e.preventDefault();
        if (searchInputRef.current) {
          searchInputRef.current.querySelector("input")?.focus();
        }
      }
    };

    window.addEventListener("keydown", handleKeyPress);
    return () => window.removeEventListener("keydown", handleKeyPress);
  }, []);

  const validateAllRules = useCallback(
    (rules: IniInfo[]) => {
      const validationResults = rules.map((rule, index) => {
        if (rule.name.includes("-comment-") || rule.isComment) return { index, rule, isValid: true, errors: [] };

        const combined = `${rule.delimiter} ${rule.value}`.trim();
        const finalResult = parseRules.getSegmentedRule(combined);
        const rows: RuleRow[] = finalResult.map((seg, idx) => ({
          delimiter: seg.delimiter,
          alias: seg.alias,
          property: seg.property,
          operator: seg.operator,
          value: seg.value,
        }));
        const syntaxResult = validateRule(rows);

        return { index: index + 1, rule, isValid: syntaxResult.isValid, errors: syntaxResult.errors };
      });

      const invalidRules = validationResults.filter((result) => !result.isValid);
      dispatch(policyEditorActions.setInvalidRules(invalidRules));
      return invalidRules;
    },
    [dispatch]
  );


  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      setLoadingMessage("Retrieving Applicability Rules...");
      dispatch(policyEditorActions.setCurrentVersionId(versionId ?? null));
      const iniPromise = versionId && currentTenant
        ? api.policyEditor.getParsedVersion(currentTenant.siteId, versionId)
        : api.policyEditor.getCompanyIniFile(currentTenant?.shortName ?? "");

      const companyIniResponse = await iniPromise;

      // Kick off package loading in the background; rules render without waiting for it
      getPackages();

      if (companyIniResponse?.isSuccess && companyIniResponse.value) {
        const processedRules = processRules(companyIniResponse.value.applicabilityRules);
        const processedRulesWithIds = processedRules.map((rule, index) =>
          tagOriginal({ ...rule, id: rule.id ?? crypto.randomUUID() }, index)
        );

        dispatch(policyEditorActions.setRules(processedRulesWithIds));
        dispatch(policyEditorActions.setPendingReplicateChanges(
          companyIniResponse.value.pendingReplicateChanges ?? { additions: [], removals: [] }
        ));

        dispatch(policyEditorActions.setPendingConfigSections(companyIniResponse.value.packageConfigSections));

        const clientConfig = companyIniResponse.value.clientConfigSection.map((line, index) => {
          const defaultRule: UnconfiguredRules | undefined = defaultNotConfiguredRules.find((r) => r.name === line.name);
          return tagOriginal(
            {
              id: line.id,
              name: line.name,
              value: line.value,
              delimiter: line.delimiter,
              applicability: line.applicability,
              inlineComment: line.inlineComment,
              description: defaultRule?.description || line.description || "",
              type: defaultRule?.type || line.type || "",
              regex: defaultRule?.regex || "",
              isComment: line.isComment ?? false,
              isDeleted: false,
              pendingChanges: false,
            } as IniInfo,
            index
          );
        });
        dispatch(policyEditorActions.setClientConfig(clientConfig));

        const sitesAndSubnets = companyIniResponse.value.sitesAndSubnetsSection
          .map((line: IniInfo) => ({
            id: line.id,
            name: line.name,
            value: line.value,
            delimiter: line.delimiter,
            inlineComment: line.inlineComment,
            emptyLinesBefore: line.emptyLinesBefore,
            isComment: line.isComment,
            pendingChanges: false,
            isDeleted: false,
          }))
          .sort((a, b) => a.name.localeCompare(b.name))
          .map((line, index) => tagOriginal(line as IniInfo, index));
        dispatch(policyEditorActions.setSitesAndSubnets(sitesAndSubnets));

        const systemConfigurations = companyIniResponse.value.systemConfigurations.map((line, index) =>
          tagOriginal(
            {
              id: line.id,
              name: line.name,
              value: line.value,
              delimiter: line.delimiter || "=",
              inlineComment: line.inlineComment,
              emptyLinesBefore: line.emptyLinesBefore,
              isComment: line.isComment,
              pendingChanges: false,
              isDeleted: false,
            } as IniInfo,
            index
          )
        );
        dispatch(policyEditorActions.setSystemConfigurations(systemConfigurations));

        const adDomains = companyIniResponse.value.activeDirectoryDomains.map((line, index) =>
          tagOriginal(
            {
              id: line.id,
              name: line.name,
              value: line.value,
              delimiter: line.delimiter,
              inlineComment: line.inlineComment,
              emptyLinesBefore: line.emptyLinesBefore,
              isComment: line.isComment,
              pendingChanges: false,
              isDeleted: false,
            } as IniInfo,
            index
          )
        );
        dispatch(policyEditorActions.setActiveDirectoryDomains(adDomains));

        const adOUs = companyIniResponse.value.activeDirectoryOUs.map((line, index) =>
          tagOriginal(
            {
              id: line.id,
              name: line.name,
              value: line.value,
              delimiter: line.delimiter,
              inlineComment: line.inlineComment,
              emptyLinesBefore: line.emptyLinesBefore,
              isComment: line.isComment,
              pendingChanges: false,
              isDeleted: false,
            } as IniInfo,
            index
          )
        );
        dispatch(policyEditorActions.setActiveDirectoryOUs(adOUs));

        const invalidRules = validateAllRules(processedRules);
        if (invalidRules.length > 0) {
          setErrorModalOpen(true);
          setLoading(false);
          return;
        }

        setLoading(false);
        setTimeout(() => {
          if (searchInputRef.current) {
            searchInputRef.current.querySelector("input")?.focus();
          }
        }, 1000);
      } else {
        setError(companyIniResponse?.failMessage || "Failed to load data");
        setLoading(false);
      }
    } catch (error) {
      setError("An unexpected error occurred while loading data.");
      setLoading(false);
    }
  }, [api.policyEditor, currentTenant, versionId, dispatch, getPackages, validateAllRules]);

  const loadingForVersionRef = useRef<string | null>(null);
  useEffect(() => {
    const desiredVersionId = versionId ?? null;
    if (loadedVersionId === desiredVersionId) return;
    if (loadingForVersionRef.current === desiredVersionId) return;

    if (loadedVersionId !== null && loadedVersionId !== desiredVersionId) {
      dispatch(policyEditorActions.resetPolicyEditorState());
      setUserClosedPackagesMissing(false);
      setPackagesMissingModalOpen(false);
    }

    loadingForVersionRef.current = desiredVersionId;
    loadData();
  }, [versionId, loadedVersionId, loadData, dispatch]);



  const sensors = useSensors(useSensor(PointerSensor), useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }));

  const rulesWithIds = rules;


  const handleReorderRows = useCallback(
  (oldIndex: number, newIndex: number) => {
    if (oldIndex === newIndex) return;

    const adjustedNewIndex = Math.max(0, Math.min(newIndex, rules.length - 1));

    dispatch(policyEditorActions.reorderRules({ oldIndex, newIndex: adjustedNewIndex }));

    listRef.current?.scrollToItem(adjustedNewIndex, "start");
  },
  [rules.length, dispatch]
);


  const handleDragEnd = (event: any) => {
    const { active, over } = event;

    if (active.id !== over.id) {
      const oldIndex = rulesWithIds.findIndex(rule => rule.id === active.id);
      const newIndex = rulesWithIds.findIndex(rule => rule.id === over.id);

      handleReorderRows(oldIndex, newIndex);
    }
  };

  const handleAddLineClick = (index: number) => {
    setInsertIndex(index);
    setModalOpen(true);
    setModalMode("add");
  };

  const handleModalClose = () => {
    setModalMode("add");
    setInsertIndex(null);
    setModalOpen(false);
  };

  const handleEditComment = (rule: IniInfo, index: number) => {
    setInsertIndex(index);
    setModalOpen(true);
    setModalMode("edit");
  };

  const handleModalSubmit = (data: IniInfo, type: "package" | "comment") => {
    if (insertIndex === null) return;

    if (type === "comment") {
      if (insertIndex !== null) {

        // every new rule has a unique id
        if (!data.id) {
          data.id = crypto.randomUUID();
        }

        if (modalMode === "edit") {
          dispatch(policyEditorActions.updateRule({ index: insertIndex, updatedRule: data }));
        } else {
          dispatch(policyEditorActions.addRule({ index: insertIndex, rule: data }));
        }
      }
      handleModalClose();
    
    } else if (type === "package") {

      if (!data.id) {
        data.id = crypto.randomUUID();
      }

      const packageName = data.name?.trim() ?? "";
      const isInReplicate = packages.some((p) => p.value?.toLowerCase() === packageName.toLowerCase());

      const selectedPackage: SelectedPackage = {
        index: insertIndex,
        rule: data,
        description: data.inlineComment || "",
        icon: "",
        isUpdating: false,
        isValid: false,
        activePackageName: isInReplicate ? undefined : packageName,
      };

      dispatch(policyEditorActions.setSelectedPackage(selectedPackage));
      navigate("/policyeditorsubject");
    }


  };

  const handleDeleteRow = (index: number) => {
    dispatch(policyEditorActions.deleteRule(index));
  };

  const handleUndoDelete = (index: number) => {
    dispatch(policyEditorActions.undoRuleDeletion(index));
  };

  const handleGoToSubject = (rule: IniInfo, index: number) => {
    const selectedPackage: SelectedPackage = {
      index: index,
      rule: rule,
      isUpdating: true,
      description: packages.find((p) => p.value === rule.name)?.description || "",
      icon: "",
      isValid: true,
    };

    dispatch(policyEditorActions.setSelectedPackage(selectedPackage));
    navigate("/policyeditorsubject");
  };

  const handleEditMissingRule = useCallback(
    (rule: MissingPackageRule) => {
      const index = rules.findIndex((r) => r.id === rule.ruleId);
      if (index < 0) return;
      const existing = rules[index];
      const matchedPackage = packages.find((p) => p.value === existing.name)
        ?? globalCatalog.find((p) => p.value === existing.name);
      const selectedPackage: SelectedPackage = {
        index,
        rule: existing,
        isUpdating: true,
        description: matchedPackage?.description || "",
        icon: matchedPackage?.iconUrl || "",
        isValid: true,
        fromMissingModal: true,
      };
      dispatch(policyEditorActions.setSelectedPackage(selectedPackage));
      setPackagesMissingModalOpen(false);
      navigate("/policyeditorsubject");
    },
    [dispatch, rules, packages, globalCatalog, navigate]
  );

  const handleDeleteMissingRule = useCallback(
    (rule: MissingPackageRule) => {
      const index = rules.findIndex((r) => r.id === rule.ruleId);
      if (index < 0) return;
      dispatch(policyEditorActions.deleteRule(index));
    },
    [dispatch, rules]
  );

  const handleToggleComment = useCallback(
    (id: string, newContent: string, isComment: boolean) => {
      const newCommentName = `New-Comment-${Date.now()}`;
      const updatedRules = rules.map((rule: IniInfo) => {
        if (rule.id !== id) return rule;

        if (isComment) {
          const restored = restoreOriginalCommentFields(rule);
          if (restored) return restored;
          return {
            ...rule,
            isComment: true,
            value: newContent,
            name: newCommentName,
            delimiter: "",
            inlineComment: "",
          };
        } else {
          // Uncommenting: if the row was originally NOT a comment, restore the load-time fields
          // so the signature matches the original and the row drops out of modifiedRows. AB#3115.
          const restored = restoreOriginalUncommentFields(rule);
          if (restored) return restored;
          // Otherwise (line added during this session) parse rule.value back into fields.
          const uncommentedContent = rule.value?.replace(/^;/, "").trim() || "";
          const validDelimiters = [" is for any ", " is ", " = "];
          let name = uncommentedContent;
          let delimiter = "";
          let value = "";
          let inlineComment = "";

          // Split on delimiter first
          for (const d of validDelimiters) {
            const parts = uncommentedContent.split(new RegExp(`\\s*${d}\\s*`, "i"));
            if (parts.length >= 2) {
              name = parts[0].trim();
              delimiter = d;
              const afterDelimiter = parts.slice(1).join(d).trim();
              const commentParts = afterDelimiter.split(/;(.+)/);
              if (commentParts.length > 1) {
                value = commentParts[0].trim();
                inlineComment = commentParts[1].trim();
              } else {
                value = afterDelimiter;
                inlineComment = "";
              }
              break;
            }
          }

          // If no delimiter is found, treat the entire content as a comment
          if (!delimiter) {
            name = uncommentedContent;
            const commentParts = uncommentedContent.split(/;(.+)/);
            if (commentParts.length > 1) {
              name = commentParts[0].trim();
              inlineComment = commentParts[1].trim();
            }
          }

          return {
            ...rule,
            isComment: false,
            name,
            delimiter,
            value,
            inlineComment,
          };
        }
      });

      dispatch(policyEditorActions.setRules(updatedRules));
      const toggledRule = updatedRules.find((r) => r.id === id);
      if (toggledRule && isRowDirty(toggledRule, updatedRules)) {
        dispatch(policyEditorActions.updateModifiedRows(id));
      } else {
        dispatch(policyEditorActions.removeModifiedRow(id));
      }

      validateAllRules(updatedRules);
    },
    [rules, dispatch, validateAllRules]
  );

  useEffect(() => {
    if (lastEditedIndex != null) {
      listRef.current?.scrollToItem(lastEditedIndex, "start");
    }
  }, [lastEditedIndex]);

  const modifiedRules = useMemo(() => {
    return rules.map((rule, index) => ({ index, rule })).filter(({ rule }) => modifiedRows.includes(rule.id!));
  }, [rules, modifiedRows]);

  if (loading) {
    return <Loading message={loadingMessage} />;
  }

  if (error) {
    return (
      <div className="denied">
        <div className="denied__content">
          <img alt="logo" src="/icon.png" style={{ float: "left", width: "50px", height: "50px" }} />
          <div className="denied__message">
            We could not retrieve the company.ini for this company.
            <br />
            Please contact the Aiden Customer Success team
          </div>
        </div>
      </div>
    );
  }

  const Row = React.memo(({ index, style }: { index: number; style: React.CSSProperties }) => {
    //const rule = rules[index];
    const rule = rulesWithIds[index];

    const packageData = useMemo(() => {
      let match: IniInfo | undefined = undefined;
      const fullMatches: IniInfo[] = [];
      const partialMatches: IniInfo[] = [];

      for (const pkgValue in packageMap) {
        const pkg = packageMap[pkgValue];
        if (isFullPatternMatch(pkg.value, rule.name)) {
          fullMatches.push(pkg);
        } else if (isPartialPatternMatch(pkg.value, rule.name)) {
          partialMatches.push(pkg);
        }
      }

      match = fullMatches.find((pkg) => pkg.iconUrl && pkg.iconUrl !== "");
      if (!match && fullMatches.length > 0) {
        match = fullMatches[0];
      }

      if (!match) {
        match = partialMatches.find((pkg) => pkg.iconUrl && pkg.iconUrl !== "");
        if (!match && partialMatches.length > 0) {
          match = partialMatches[0];
        }
      }

      return match;
    }, [rule.name]);

    const iconUrl = packageData?.iconUrl || "";

    return (
      <SortableRow
        key={rule.id}
        id={rule.id!}       
        ruleId={rule.id!}       
        rule={rule}
        index={index}
        style={style}
        modifiedRows={modifiedRows}
        onAdd={handleAddLineClick}
        onDelete={() => handleDeleteRow(index)}
        onUndoDelete={() => handleUndoDelete(index)}
        onGoToSubject={() => handleGoToSubject(rule, index)}
        onEditComment={() => handleEditComment(rule, index)}
        searchTerm={searchTerm}
        onReorder={handleReorderRows}
        totalRows={rules.length}
        packageIcon={iconUrl}
        onToggleComment={handleToggleComment}
        readOnlyViewer={readOnlyViewer}
      />
    );
  });

  return (
    <ThemeProvider theme={muiTheme}>
      <Box sx={{ color: "text.primary" }}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <Box display="flex" gap={1} alignItems={"center"}>
            <SearchBar
              ref={searchInputRef}
              searchResultsCount={searchResults.length}
              currentResultIndex={currentResultIndex}
              onSearch={handleSearch}
              onPrev={handlePrevResult}
              onNext={handleNextResult}
              onKeyDown={handleKeyDown}
            />
          </Box>
          <Box display="flex" gap={1} alignItems={"center"}>
            {invalidRules.length > 0 && (
              <Button
                variant="text"
                sx={{
                  backgroundColor: "#d5878b",
                  color: "#000",
                  "&:hover": { backgroundColor: "#ca6469" },
                  textTransform: "none",
                }}
                onClick={() => {
                  setErrorModalOpen(true);
                }}
              >
                <b style={{ marginRight: "3px" }}>{invalidRules.length} </b> syntax {invalidRules.length === 1 ? "error" : "errors"}
              </Button>
            )}
            {packagesMissingRules.length > 0 && (
              <Button
                variant="text"
                sx={{
                  backgroundColor: "#d5878b",
                  color: "#000",
                  "&:hover": { backgroundColor: "#ca6469" },
                  textTransform: "none",
                }}
                onClick={() => {
                  setPackagesMissingModalOpen(true);
                }}
              >
                <b style={{ marginRight: "3px" }}>{packagesMissingRules.length}</b>{" "}
                {packagesMissingRules.length === 1 ? "package missing" : "packages missing"}
              </Button>
            )}
            {modifiedRows.length > 0 && (
              <Button
                variant="text"
                sx={{
                  backgroundColor: "#fbc02d",
                  color: "#000",
                  "&:hover": { backgroundColor: "#e3ac27" },
                  textTransform: "none",
                }}
                onClick={() => setPendingChangesModalOpen(true)}
              >
                <b style={{ marginRight: "3px" }}>{modifiedRules.length}</b> Rule {modifiedRules.length === 1 ? "Change" : "Changes"}
              </Button>
            )}
          </Box>
        </Box>

        <TableContainer
          sx={{
            maxHeight: "85vh",
            border: "1px solid #333",
            backgroundColor: "#1b1a19",
            borderRadius: "8px",
            overflowY: "auto",
            overflowX: "hidden",
          }}
          component="div"
        >
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleDragEnd}
            onDragStart={() => {
              setSearchTerm("");
              setSearchResults([]);
              setCurrentResultIndex(-1);
            }}
          >
          <SortableContext
            items={rulesWithIds.filter(rule => rule.id).map(rule => rule.id!)}
            strategy={verticalListSortingStrategy}
          >
              <VariableSizeList height={window.innerHeight} width="100%" itemCount={rules.length} itemSize={() => 50} ref={listRef}>
                {({ index, style }) => <Row index={index} style={style} />}
              </VariableSizeList>
            </SortableContext>
          </DndContext>
        </TableContainer>
      </Box>
      <AddPackageModal
        open={isModalOpen}
        onClose={handleModalClose}
        onSubmit={handleModalSubmit}
        packages={packages}
        globalCatalog={globalCatalog}
        initialComment={modalMode === "edit" ? rules[insertIndex!]?.value || "" : ""}
        initialEmptyLinesBefore={modalMode === "edit" && insertIndex !== null ? rules[insertIndex].emptyLinesBefore : 0}
        mode={modalMode}
      />
      <InvalidRulesModal
        open={isErrorModalOpen}
        onNavigate={handleGoToSubject}
        onClose={() => setErrorModalOpen(false)}
        invalidRules={invalidRules}
      />
      <PendingChangesModal
        open={isPendingChangesModalOpen}
        onClose={() => setPendingChangesModalOpen(false)}
        modifiedRules={modifiedRules}
        onNavigate={handleGoToSubject}
      />
      <PackagesNotInReplicateModal
        open={isPackagesMissingModalOpen}
        onClose={() => {
          setPackagesMissingModalOpen(false);
          setUserClosedPackagesMissing(true);
        }}
        missingRules={packagesMissingRules}
        onEditRule={handleEditMissingRule}
        onDeleteRule={handleDeleteMissingRule}
        readOnlyViewer={readOnlyViewer}
      />
    </ThemeProvider>
  );
};

export default MainApplicabilityRulesTab;
